export const MAGIC_FORM_REVISION_CONFLICT_ERROR_CODE =
  "MAGIC_FORM_REVISION_CONFLICT" as const;

/**
 * Thrown by MagicForm submit when the backend atom changed while the user was editing.
 *
 * UI code (e.g. MagicSaveButton) should catch and swallow this error because the
 * conflict modal will be opened by setting `suggestedChanges`.
 */
export class MagicFormRevisionConflictError extends Error {
  public readonly code = MAGIC_FORM_REVISION_CONFLICT_ERROR_CODE;

  constructor(message = "Revision conflict") {
    super(message);
    this.name = "MagicFormRevisionConflictError";
  }
}

export function isMagicFormRevisionConflictError(
  error: unknown,
): error is MagicFormRevisionConflictError {
  if (error instanceof MagicFormRevisionConflictError) {
    return true;
  }
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const maybe = error as { code?: unknown };
  return maybe.code === MAGIC_FORM_REVISION_CONFLICT_ERROR_CODE;
}

export const MAGIC_FORM_VALIDATION_ERROR_CODE =
  "MAGIC_FORM_VALIDATION" as const;

/**
 * Thrown by MagicForm submit when the form's `validate` reports messages:
 * nothing is saved. The save buttons are disabled while the form is invalid,
 * so this surfaces only where a save bypasses them, for example an Enter
 * handler that calls `submit()`.
 */
export class MagicFormValidationError extends Error {
  public readonly code = MAGIC_FORM_VALIDATION_ERROR_CODE;

  /** The messages `validate` returned, in its order. */
  public readonly messages: readonly string[];

  constructor(messages: readonly string[]) {
    super(messages.join("\n"));
    this.name = "MagicFormValidationError";
    this.messages = messages;
  }
}

export function isMagicFormValidationError(
  error: unknown,
): error is MagicFormValidationError {
  if (error instanceof MagicFormValidationError) {
    return true;
  }
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const maybe = error as { code?: unknown };
  return maybe.code === MAGIC_FORM_VALIDATION_ERROR_CODE;
}
